import { newsList, type NewsArticle } from "@/mocks/news";
import { categories, type Category } from "@/mocks/categories";
import { topics, type Topic } from "@/mocks/topics";

export type SortKey = "latest" | "hot";

export function getArticleById(id: string | undefined): NewsArticle | undefined {
  if (!id) return undefined;
  return newsList.find((item) => item.id === id);
}

export function getCategoryById(id: string | undefined): Category | undefined {
  if (!id) return undefined;
  return categories.find((item) => item.id === id);
}

export function getTopicById(id: string | undefined): Topic | undefined {
  if (!id) return undefined;
  return topics.find((item) => item.id === id);
}

export function getTopicArticles(topic: Topic): NewsArticle[] {
  const ids = topic.sections.flatMap((section) => section.newsIds);
  return ids
    .map((id) => getArticleById(id))
    .filter((item): item is NewsArticle => Boolean(item));
}

export function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hour = `${date.getHours()}`.padStart(2, "0");
  const minute = `${date.getMinutes()}`.padStart(2, "0");
  return `${month}-${day} ${hour}:${minute}`;
}

export function formatFullDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${date.getFullYear()} 年 ${date.getMonth() + 1} 月 ${date.getDate()} 日 ${`${date.getHours()}`.padStart(2, "0")}:${`${date.getMinutes()}`.padStart(2, "0")}`;
}

export function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)} 万`;
  }
  return `${views}`;
}

export function sortArticles(list: NewsArticle[], sort: SortKey): NewsArticle[] {
  const next = [...list];
  if (sort === "hot") {
    next.sort((a, b) => b.views - a.views);
    return next;
  }
  next.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
  return next;
}

export function filterArticles(params: {
  categoryId?: string;
  tag?: string;
  keyword?: string;
}): NewsArticle[] {
  const { categoryId, tag, keyword } = params;
  let list = newsList;

  if (categoryId) {
    list = list.filter((item) => item.categoryId === categoryId);
  }
  if (tag && tag !== "全部") {
    list = list.filter((item) => item.tags.includes(tag));
  }
  const q = keyword?.trim().toLowerCase();
  if (q) {
    list = list.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        item.summary.toLowerCase().includes(q) ||
        item.tags.some((t) => t.toLowerCase().includes(q)) ||
        item.content.some((p) => p.toLowerCase().includes(q)),
    );
  }
  return list;
}

export function searchArticles(keyword: string, sort: SortKey): NewsArticle[] {
  return sortArticles(filterArticles({ keyword }), sort);
}

export function getRelatedArticles(article: NewsArticle, limit = 3): NewsArticle[] {
  const scored = newsList
    .filter((item) => item.id !== article.id)
    .map((item) => {
      let score = 0;
      if (item.categoryId === article.categoryId) score += 3;
      score += item.tags.filter((tag) => article.tags.includes(tag)).length * 2;
      score += item.topicIds.filter((id) => article.topicIds.includes(id)).length;
      return { item, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || b.item.views - a.item.views)
    .slice(0, limit)
    .map((entry) => entry.item);

  if (scored.length < limit) {
    const fallback = newsList
      .filter((item) => item.id !== article.id && !scored.some((s) => s.id === item.id))
      .sort((a, b) => b.views - a.views)
      .slice(0, limit - scored.length);
    return [...scored, ...fallback];
  }
  return scored;
}

export function getHotArticles(limit = 5): NewsArticle[] {
  return [...newsList].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getLatestArticles(limit = 6): NewsArticle[] {
  return sortArticles(newsList, "latest").slice(0, limit);
}

export function getFeaturedArticles(limit = 3): NewsArticle[] {
  const featured = newsList.filter((item) => item.featured);
  if (featured.length >= limit) return featured.slice(0, limit);
  return getLatestArticles(limit);
}

export function getCategoryCount(categoryId: string): number {
  return newsList.filter((item) => item.categoryId === categoryId).length;
}